import { CoreQuery, Query } from "@gen/app-query";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import {
  loadConnectAndInitialize,
  type StripeConnectInstance,
} from "@stripe/connect-js";
import {
  ConnectBalances,
  ConnectComponentsProvider,
} from "@stripe/react-connect-js";
import { useEffect, useRef, useState } from "react";

import { HeadingContainer } from "~/components/heading_container";
import { PublicSvgIcon } from "~/components/public_svg_icon";
import { StatusChip } from "~/components/status_chip";
import { stripeConnectAppearance, stripeConnectFonts } from "~/theme";

interface AccountBalanceProps {
  clientId: string;
  publishableKey: string;
  isAdmin: boolean;
  isPayoutsPaused?: boolean;
}

export function AccountBalance({
  clientId,
  publishableKey,
  isAdmin,
  isPayoutsPaused = false,
}: AccountBalanceProps) {
  const theme = useTheme();
  const [connectInstance, setConnectInstance] =
    useState<StripeConnectInstance | null>(null);

  const initializedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const key = `${clientId}:${publishableKey}`;
    if (initializedKeyRef.current === key) {
      return;
    }
    initializedKeyRef.current = key;

    setConnectInstance(
      loadConnectAndInitialize({
        publishableKey,
        fetchClientSecret: async () =>
          (
            await (isAdmin
              ? CoreQuery.Client().admin_getClientAccountSession(clientId)
              : Query.Client().core_getClientAccountSession(clientId))
          ).client_secret,
        fonts: stripeConnectFonts,
        appearance: stripeConnectAppearance,
      }),
    );
  }, [publishableKey, clientId, isAdmin]);

  if (connectInstance === null) {
    return null;
  }

  return (
    <Stack
      id="account_balance"
      spacing={3}
      sx={{
        p: 2.5,
        borderRadius: 2,
        backgroundColor: "primary._states.hover",
        overflowX: "auto",
      }}
    >
      <HeadingContainer
        title={
          <>
            <PublicSvgIcon name="bar_chart_4_bars" /> Account Balance
            {isPayoutsPaused && (
              <StatusChip
                label="On Hold"
                color={theme.palette.error.main}
                halo={false}
                sx={{ typography: "body2", ml: 1 }}
                dataTestId="account-balance-paused-chip"
              />
            )}
          </>
        }
        variant="h5"
        borderType="solid"
      />
      <ConnectComponentsProvider connectInstance={connectInstance}>
        <ConnectBalances />
      </ConnectComponentsProvider>
    </Stack>
  );
}
